import React, { useState, useEffect, useRef } from 'react';
import { ChevronLeft, Printer, Package, MapPin, Plane, Users } from 'lucide-react';
import { useReactToPrint } from 'react-to-print';
import axios from 'axios';
import PrintSettings from './PrintSettings';
import DeliveryPointsList from './DeliveryPointsList';
import PrintTemplate from './PrintTemplate';

function PrintDeliveriesContent({ 
  userId, 
  isAdminView = false, 
  isAllUsers = false,
  adminUserId = null,
  sellerName = '',
  onBackClick 
}) {
  // Lima deliveries
  const [selectedLimaDeliveries, setSelectedLimaDeliveries] = useState([]);
  const [limaDeliveries, setLimaDeliveries] = useState([]);
  
  // Province deliveries
  const [selectedProvinceDeliveries, setSelectedProvinceDeliveries] = useState([]);
  const [provinceDeliveries, setProvinceDeliveries] = useState([]);
  
  // Dropshipper deliveries
  const [selectedDropshipLima, setSelectedDropshipLima] = useState([]);
  const [dropshipLima, setDropshipLima] = useState([]);
  
  const [selectedDropshipProvince, setSelectedDropshipProvince] = useState([]);
  const [dropshipProvince, setDropshipProvince] = useState([]);
  
  const [loading, setLoading] = useState(true);
  const [printerType, setPrinterType] = useState('a4');
  const printRef = useRef();

  useEffect(() => {
    fetchDeliveryPoints();
  }, [userId, isAllUsers, adminUserId]);

  async function fetchDeliveryPoints() {
    setLoading(true);
    try {
      let response;
      const token = localStorage.getItem('token');
      const authConfig = { headers: token ? { Authorization: `Bearer ${token}` } : {} };
      
      if (isAllUsers && adminUserId) {
        // Fetch all deliveries for provider
        response = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/delivery_points/provider/${adminUserId}`
        );
        
        // Response structure: { store_deliveries, store_province_deliveries, dropship_deliveries, dropship_province_deliveries }
        setLimaDeliveries(response.data.store_deliveries || []);
        setProvinceDeliveries(response.data.store_province_deliveries || []);
        setDropshipLima(response.data.dropship_deliveries || []);
        setDropshipProvince(response.data.dropship_province_deliveries || []);
        
      } else if (isAdminView) {
        // Fetch deliveries for specific user
        response = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/user/${userId}`,
          authConfig
        );
        
        const allDeliveries = response.data.delivery_points || [];
        
        // Filter out deliveries with provider_id (handled by provider)
        const userOwnDeliveries = allDeliveries.filter(dp => !dp.provider_id);
        setLimaDeliveries(userOwnDeliveries);
        
        // Fetch province deliveries
        try {
          const provinceResponse = await axios.get(
            `${process.env.REACT_APP_API_URL}/api/user/${userId}/province-deliveries`
          );
          const allProvinceDeliveries = provinceResponse.data.province_deliveries || [];
          const userOwnProvince = allProvinceDeliveries.filter(dp => !dp.provider_id);
          setProvinceDeliveries(userOwnProvince);
        } catch (err) {
          console.error('Error fetching province deliveries:', err);
        }
        
      } else {
        // Regular user view
        response = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/user/${userId}`,
          authConfig
        );
        
        const allDeliveries = response.data.delivery_points || [];
        const userOwnDeliveries = allDeliveries.filter(dp => !dp.provider_id);
        setLimaDeliveries(userOwnDeliveries);
        
        // Fetch province deliveries
        try {
          const provinceResponse = await axios.get(
            `${process.env.REACT_APP_API_URL}/api/user/${userId}/province-deliveries`
          );
          const allProvinceDeliveries = provinceResponse.data.province_deliveries || [];
          const userOwnProvince = allProvinceDeliveries.filter(dp => !dp.provider_id);
          setProvinceDeliveries(userOwnProvince);
        } catch (err) {
          console.error('Error fetching province deliveries:', err);
        }
      }
      
    } catch (error) {
      console.error('Error fetching delivery points:', error);
    } finally {
      setLoading(false);
    }
  }

  function handleSelectionChange(dp, type) {
    const setters = {
      'lima': setSelectedLimaDeliveries,
      'province': setSelectedProvinceDeliveries,
      'dropship-lima': setSelectedDropshipLima,
      'dropship-province': setSelectedDropshipProvince
    };
    
    const setter = setters[type];
    if (setter) {
      setter(prev => {
        if (prev.some(item => item.id === dp.id)) {
          return prev.filter(item => item.id !== dp.id);
        } else {
          return [...prev, dp];
        }
      });
    }
  }

  function handleSelectAll(type) {
    const configs = {
      'lima': { deliveries: limaDeliveries, selected: selectedLimaDeliveries, setter: setSelectedLimaDeliveries },
      'province': { deliveries: provinceDeliveries, selected: selectedProvinceDeliveries, setter: setSelectedProvinceDeliveries },
      'dropship-lima': { deliveries: dropshipLima, selected: selectedDropshipLima, setter: setSelectedDropshipLima },
      'dropship-province': { deliveries: dropshipProvince, selected: selectedDropshipProvince, setter: setSelectedDropshipProvince }
    };
    
    const config = configs[type];
    if (config) {
      if (config.selected.length === config.deliveries.length) {
        config.setter([]);
      } else {
        config.setter([...config.deliveries]);
      }
    }
  }

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    pageStyle: printerType === 'thermal' 
      ? `
        @page {
          size: 80mm auto;
          margin: 0;
        }
        @media print {
          body { margin: 0; }
        }
      `
      : `
        @page {
          size: A4 portrait;
          margin: 10mm;
        }
        @media print {
          body { margin: 0; }
        }
      `
  });

  const totalSelected = 
    selectedLimaDeliveries.length + 
    selectedProvinceDeliveries.length + 
    selectedDropshipLima.length + 
    selectedDropshipProvince.length;

  const hasStoreDeliveries = limaDeliveries.length > 0 || provinceDeliveries.length > 0;
  const hasDropshipDeliveries = dropshipLima.length > 0 || dropshipProvince.length > 0;

  return (
    <div className="h-screen flex flex-col bg-gradient-to-br from-slate-50 via-white to-slate-50">
      {/* Fixed Header */}
      <div className="flex-shrink-0 bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <button
              onClick={onBackClick}
              className="flex items-center gap-2 text-slate-700 hover:text-brand-primary transition-colors duration-200 font-montserrat"
            >
              <ChevronLeft className="w-5 h-5" />
              <span className="text-sm sm:text-base">Volver</span>
            </button>
            <div className="text-center">
              <h1 className="text-lg sm:text-xl font-extrabold text-brand-primary font-montserrat">
                Rotulado
              </h1>
              {isAdminView && sellerName && (
                <p className="text-xs text-slate-600 font-montserrat">
                  {sellerName}
                </p>
              )}
            </div>
            <div className="w-20"></div>
          </div>
        </div>
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-4xl mx-auto px-4 py-6 sm:px-6 lg:px-8">
          
          {/* Instructions Card */}
          <div className="bg-gradient-to-r from-brand-primary/10 to-brand-gold/10 border-l-4 border-brand-primary rounded-xl p-4 mb-6">
            <div className="flex items-start gap-3">
              <Package className="w-5 h-5 text-brand-primary flex-shrink-0 mt-0.5" />
              <div>
                <h2 className="text-base font-bold text-brand-primary font-montserrat mb-1">
                  Selecciona las entregas a imprimir
                </h2>
                <p className="text-sm text-slate-700 font-montserrat">
                  Escoge las entregas que deseas imprimir en formato de etiquetas
                </p>
              </div>
            </div>
          </div>

          {/* Printer Settings */}
          <PrintSettings 
            printerType={printerType}
            onPrinterTypeChange={setPrinterType}
          />

          {loading && (
            <div className="flex items-center justify-center py-12">
              <div className="w-12 h-12 border-4 border-brand-primary border-t-transparent rounded-full animate-spin"></div>
            </div>
          )}

          {!loading && !hasStoreDeliveries && !hasDropshipDeliveries && (
            <div className="bg-white rounded-2xl border border-slate-200 p-8 text-center">
              <Package className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-bold text-slate-900 font-montserrat mb-2">
                No hay entregas disponibles
              </h3>
              <p className="text-sm text-slate-600 font-montserrat">
                No hay entregas programadas para hoy
              </p>
            </div>
          )}

          {/* Store Deliveries Section */}
          {!loading && hasStoreDeliveries && (
            <div className="mb-8">
              <div className="flex items-center gap-2 mb-4">
                <Package className="w-5 h-5 text-brand-primary" />
                <h2 className="text-xl font-bold text-slate-900 font-montserrat">
                  Entregas de Tiendas
                </h2>
              </div>

              {/* Lima Store Deliveries */}
              {limaDeliveries.length > 0 && (
                <div className="mb-6">
                  <DeliveryPointsList
                    title="Lima"
                    icon={<MapPin className="w-5 h-5 text-brand-primary" />}
                    deliveryPoints={limaDeliveries}
                    selectedDeliveryPoints={selectedLimaDeliveries}
                    onSelectionChange={(dp) => handleSelectionChange(dp, 'lima')}
                    onSelectAll={() => handleSelectAll('lima')}
                    allSelected={selectedLimaDeliveries.length === limaDeliveries.length}
                    loading={false}
                    colorTheme="primary"
                  />
                </div>
              )}

              {/* Province Store Deliveries */}
              {provinceDeliveries.length > 0 && (
                <div className="mb-6">
                  <DeliveryPointsList
                    title="Provincia"
                    icon={<Plane className="w-5 h-5 text-brand-gold" />}
                    deliveryPoints={provinceDeliveries}
                    selectedDeliveryPoints={selectedProvinceDeliveries}
                    onSelectionChange={(dp) => handleSelectionChange(dp, 'province')}
                    onSelectAll={() => handleSelectAll('province')}
                    allSelected={selectedProvinceDeliveries.length === provinceDeliveries.length}
                    loading={false}
                    colorTheme="gold"
                    isProvince={true}
                  />
                </div>
              )}
            </div>
          )}

          {/* Dropshipper Deliveries Section */}
          {!loading && hasDropshipDeliveries && (
            <div className="mb-8">
              <div className="flex items-center gap-2 mb-4">
                <Users className="w-5 h-5 text-brand-green" />
                <h2 className="text-xl font-bold text-slate-900 font-montserrat">
                  Entregas Dropshipping
                </h2>
              </div>

              {/* Lima Dropship Deliveries */}
              {dropshipLima.length > 0 && (
                <div className="mb-6">
                  <DeliveryPointsList
                    title="Lima - Dropshipping"
                    icon={<MapPin className="w-5 h-5 text-brand-green" />}
                    deliveryPoints={dropshipLima}
                    selectedDeliveryPoints={selectedDropshipLima}
                    onSelectionChange={(dp) => handleSelectionChange(dp, 'dropship-lima')}
                    onSelectAll={() => handleSelectAll('dropship-lima')}
                    allSelected={selectedDropshipLima.length === dropshipLima.length}
                    loading={false}
                    colorTheme="green"
                  />
                </div>
              )}

              {/* Province Dropship Deliveries */}
              {dropshipProvince.length > 0 && (
                <div className="mb-6">
                  <DeliveryPointsList
                    title="Provincia - Dropshipping"
                    icon={<Plane className="w-5 h-5 text-brand-green" />}
                    deliveryPoints={dropshipProvince}
                    selectedDeliveryPoints={selectedDropshipProvince}
                    onSelectionChange={(dp) => handleSelectionChange(dp, 'dropship-province')}
                    onSelectAll={() => handleSelectAll('dropship-province')}
                    allSelected={selectedDropshipProvince.length === dropshipProvince.length}
                    loading={false}
                    colorTheme="green"
                    isProvince={true}
                  />
                </div>
              )}
            </div>
          )}

        </div>
      </div>

      {/* Fixed Footer - Print Button */}
      <div className="flex-shrink-0 bg-white border-t border-slate-200 shadow-lg">
        <div className="max-w-4xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
          <button
            onClick={handlePrint}
            disabled={totalSelected === 0}
            className="w-full bg-gradient-to-r from-brand-primary to-brand-gold text-white font-semibold py-3 px-4 rounded-xl transition-all duration-200 shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed font-montserrat flex items-center justify-center gap-2"
          >
            <Printer className="w-5 h-5" />
            <span>
              Imprimir {totalSelected > 0 && `(${totalSelected})`}
            </span>
          </button>
        </div>
      </div>

      {/* Hidden Print Content */}
      <div style={{ display: 'none' }}>
        <PrintTemplate
          ref={printRef}
          limaDeliveries={selectedLimaDeliveries}
          provinceDeliveries={selectedProvinceDeliveries}
          dropshipLima={selectedDropshipLima}
          dropshipProvince={selectedDropshipProvince}
          printerType={printerType}
        />
      </div>
    </div>
  );
}

export default PrintDeliveriesContent;
